import { useCallback, useEffect, useRef, useState } from 'react'
import { apiClient } from '@/lib/api'

// ─── Notification inbox (UX-192) ──────────────────────────────────────────────
// The signed-in user's own notifications: workspace alerts (new lead, missed
// call, invoice paid/overdue, job completed) and events on their jobs. Feeds
// the bell and /notifications. Every row can also arrive as a push message.

export interface InboxItem {
  id: string
  eventType: string | null
  kind: string | null
  title: string
  body: string
  link: string | null
  jobId: string | null
  readAt: string | null
  createdAt: string
}

interface InboxResponse {
  data: { items: InboxItem[]; unreadCount: number; hasMore: boolean }
}

const POLL_MS = 60_000

export function useNotificationInbox({ limit = 20, unreadOnly = false, poll = true } = {}) {
  const [items, setItems] = useState<InboxItem[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [loaded, setLoaded] = useState(false)
  // Raw error: QueryState / humanError turn it into words.
  const [error, setError] = useState<unknown>(null)
  const active = useRef(true)

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get<InboxResponse>('/api/notifications/inbox', {
        params: { limit, ...(unreadOnly ? { unread: 'true' } : {}) },
      })
      if (!active.current) return
      setItems(res.data.data.items)
      setUnreadCount(res.data.data.unreadCount)
      setHasMore(res.data.data.hasMore)
      setLoaded(true)
      setError(null)
    } catch (err) {
      if (active.current) setError(err)
    } finally {
      if (active.current) setLoading(false)
    }
  }, [limit, unreadOnly])

  useEffect(() => {
    active.current = true
    setLoading(true)
    void load()
    if (!poll) return () => { active.current = false }
    const timer = setInterval(() => {
      if (typeof document === 'undefined' || document.visibilityState === 'visible') void load()
    }, POLL_MS)
    // A tapped push notification brings the app forward — catch up at once.
    const onFocus = () => void load()
    window.addEventListener('focus', onFocus)
    return () => {
      active.current = false
      clearInterval(timer)
      window.removeEventListener('focus', onFocus)
    }
  }, [load, poll])

  const loadMore = useCallback(async () => {
    const last = items[items.length - 1]
    if (!last) return
    setLoadingMore(true)
    try {
      const res = await apiClient.get<InboxResponse>('/api/notifications/inbox', {
        params: { limit, before: last.createdAt, ...(unreadOnly ? { unread: 'true' } : {}) },
      })
      setItems((prev) => [...prev, ...res.data.data.items])
      setHasMore(res.data.data.hasMore)
    } catch (err) {
      setError(err)
    } finally {
      setLoadingMore(false)
    }
  }, [items, limit, unreadOnly])

  const markRead = useCallback(async (id: string) => {
    const target = items.find((i) => i.id === id)
    if (!target || target.readAt) return
    const now = new Date().toISOString()
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, readAt: now } : i)))
    setUnreadCount((c) => Math.max(0, c - 1))
    try {
      await apiClient.post(`/api/notifications/inbox/${id}/read`)
    } catch {
      void load()
    }
  }, [items, load])

  const markAllRead = useCallback(async () => {
    const now = new Date().toISOString()
    setItems((prev) => prev.map((i) => (i.readAt ? i : { ...i, readAt: now })))
    setUnreadCount(0)
    try {
      await apiClient.post('/api/notifications/inbox/read-all')
    } catch {
      void load()
    }
  }, [load])

  return {
    items,
    unreadCount,
    hasMore,
    loading,
    loadingMore,
    error,
    /** QueryState shape: data stays null until the first successful load. */
    query: { loading, error, data: loaded ? items : null, refetch: load },
    refetch: load,
    loadMore,
    markRead,
    markAllRead,
  }
}

/** Where tapping a notification goes. */
export function inboxTarget(item: InboxItem): string | null {
  if (item.link && item.link.startsWith('/') && !item.link.startsWith('//')) return item.link
  if (item.jobId) return `/jobs/${item.jobId}`
  return null
}
